import React from 'react';
import { Box, Button, Heading, Stack, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';

import { Spinner } from '../../../atoms/spinner';
import { Link } from '../../../atoms/link';
import { Title } from '../../../atoms/Title';

export interface IMfaMethodSelectionOptions {
  id: string;
  onClick: () => void;
  icon: React.ReactElement;
  content: React.ReactElement;
}
export interface IMfaMethodSelection {
  options: IMfaMethodSelectionOptions[];
  onSkip: () => void;
  canSkip?: boolean;
}

export function MfaMethodSelection({ options, onSkip, canSkip }: IMfaMethodSelection): React.ReactElement {
  const title = 'Set up MFA on your account';

  return (
    <>
      <Title title={title} />

      <Heading as="h1" mb={6}>
        {title}
      </Heading>

      <Text>
        Keep your account secure by setting up Multi-factor Authentication (MFA). This prevents unauthorised people from
        accessing your account by requiring a code from a device only you have access to. Find out more in our{' '}
        <Link href="https://www.enroly.com/faqs/mfa" isExternal>
          Multi-factor authentication (MFA) FAQs
        </Link>
        .
      </Text>

      <Stack spacing={6} mt={8}>
        {options.length ? (
          options.map((option, index) => (
            <Button
              key={index}
              id={option.id}
              data-selector={`mfa-option-${option.id}`}
              onClick={option.onClick}
              leftIcon={
                <Box fontSize={{ base: '1.25rem', sm: '1.75rem' }} mr={1}>
                  {option.icon}
                </Box>
              }
              rightIcon={<FontAwesomeIcon icon={faAngleRight} className={'utility-icon'} />}
              size={'lg'}
              variant={'outline'}
              colorScheme={'gray'}
              whiteSpace={'normal'}
              textAlign={'left'}
              display={'flex'}
              height={24}
              px={{ base: 3, sm: 6 }}
            >
              <Box as="span" display="block" flexGrow={1}>
                {option.content}
              </Box>
            </Button>
          ))
        ) : (
          <Spinner />
        )}

        {canSkip !== false && (
          <Button onClick={onSkip} data-selector={'skip-mfa'} size={'lg'}>
            Skip for now
          </Button>
        )}
      </Stack>
    </>
  );
}

export default MfaMethodSelection;
